<template>
    <div class="w-full relative overflow-hidden" ref="cardcontainer">
        <div
            class="grid grid-cols-12 gap-1 @container"
            ref="cardgrid"
            :class="{
                'mask overflow-hidden cursor-pointer':
                    !showFullCard && colLayout === 'horizontal',
            }"
            v-on="!showFullCard ? { click: toggleShowFullCard } : {}"
        >
            <div class="col-span-12 @3xl:col-span-4 relative">
                <!-- START BOOKING INFO CONTAINER: MY POINTS -->
                <BookingInfoContainer
                    :heading="`My ${$filters.ucWords(propertyProgram.name)} Points`"
                    :width="widthSmall"
                >
                    <PropertyCardMyPoints
                        :programImage="propertyProgram.image"
                        :pointsForProgram="pointsForProgram"
                        :orientation="colLayout"
                    />
                </BookingInfoContainer>

                <div
                    class="absolute z-10 left-1/2 -translate-x-1/2 -bottom-[20px] @3xl:left-auto @3xl:bottom-auto @3xl:translate-x-0 @3xl:top-1/2 @3xl:-translate-y-1/2 @3xl:-right-[22px]"
                    aria-hidden="true"
                >
                    <div
                        class="w-10 h-10 bg-white dark:bg-slate-800 rounded-full shadow dark:shadow-slate-900/50 border border-slate-200 justify-center items-center gap-2.5 flex text-slate-500"
                    >
                        <i class="fa-duotone fa-plus"></i>
                    </div>
                </div>
            </div>
            <!-- END BOOKING INFO CONTAINER: MY POINTS -->
            <!-- START BOOKING INFO CONTAINER: ADDITIONAL OPTIONS -->
            <div class="col-span-12 @3xl:col-span-4 relative">
                <BookingInfoContainer
                    heading="Transfer Partner Options"
                    :width="widthMedium"
                >
                    <PropertyCardTransferOptions
                        v-if="!noTransferPointsAvailable"
                        :programName="propertyProgram.name"
                        :programImage="propertyProgram.image"
                        :sourceProgramUserPoints="filteredUserPoints"
                    />
                    <p v-else class="text-slate-500 text-sm font-normal">
                        None of your current points transfer to
                        {{ propertyProgram.name }}.
                    </p>
                    <!-- Start need more points card -->
                    <CardRecommendation
                        class="mt-8 lg:mt-16"
                        v-if="usersWhoNeedCardRecs.length"
                        :program="propertyProgram"
                        :usersWhoNeedCardRecs="usersWhoNeedCardRecs"
                        :allUsers="pointsForProgram"
                    />
                    <!-- end need more points card -->
                </BookingInfoContainer>
                <div
                    class="absolute left-1/2 -translate-x-1/2 -bottom-[20px] @3xl:left-auto @3xl:bottom-auto @3xl:translate-x-0 @3xl:top-1/2 @3xl:-translate-y-1/2 @3xl:-right-[22px] z-10"
                    aria-hidden="true"
                >
                    <div
                        class="w-10 h-10 bg-white dark:bg-slate-800 rounded-full shadow dark:shadow-slate-900/50 border border-slate-200 justify-center items-center gap-2.5 flex text-slate-500"
                    >
                        <i
                            class="fa-light fa-arrow-right rotate-90 @3xl:rotate-0"
                        ></i>
                    </div>
                </div>
            </div>
            <!-- END BOOKING INFO CONTAINER: ADDITIONAL OPTIONS -->
            <!-- START BOOKING INFO CONTAINER: STAY PRICE -->
            <div class="col-span-12 @3xl:col-span-4">
                <BookingInfoContainer
                    heading="Points Needed for Stay"
                    :width="widthSmall"
                >
                    <PropertyCardStayPrice
                        :totalPointsCost="totalPointsCost"
                        :propertyProgram="propertyProgram"
                        :programPoints="pointsForProgram"
                        :sourceProgramUserPoints="filteredUserPoints"
                        @pointsCalculated="handlePointsCalculated"
                        :orientation="colLayout"
                    />
                    <div
                        class="w-80 h-16 pt-8 flex-col justify-start items-start inline-flex"
                    >
                        <router-link
                            :to="urlParams"
                            class="w-36 h-9 px-4 py-2 bg-orange-600 rounded shadow justify-start items-start gap-2 inline-flex text-white text-sm font-medium leading-tight transition-colors hover:bg-red-400 active:bg-red-400 focus:outline-slate-200 disabled:opacity-50 disabled:bg-orange-600"
                        >
                            View availability
                        </router-link>
                    </div>
                </BookingInfoContainer>
            </div>
            <!-- END BOOKING INFO CONTAINER: STAY PRICE -->
        </div>
        <button
            class="transition-all p-2 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 justify-center items-center gap-2.5 flex absolute bottom-5 right-7 hover:border-slate-300 hover:bg-slate-200 active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
            @click="toggleShowFullCard"
            v-if="colLayout === 'horizontal'"
        >
            <i
                class="fa-solid"
                :class="{
                    'fa-chevron-down': !showFullCard,
                    'fa-chevron-up': showFullCard,
                }"
            ></i>
        </button>
    </div>
</template>

<script>
import { computed, ref, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import { usePointsStore } from '@/store/points';
import BookingInfoContainer from '@/components/hotels/booking-info-container/booking-info-container.vue';
import PropertyCardMyPoints from '@/components/hotels/search/property-card/property-card-my-points/property-card-my-points.vue';
import PropertyCardTransferOptions from '@/components/hotels/search/property-card/property-card-transfer-options.vue';
import PropertyCardStayPrice from '@/components/hotels/search/property-card/property-card-stay-price/property-card-stay-price.vue';
import CardRecommendation from '@/components/hotels/search/property-card/card-recommendation/card-recommendation.vue';
import { useRewardsAndTransferPartners } from '@/composables/useRewardsAndTransferPartners.vue';

export default {
    components: {
        BookingInfoContainer,
        PropertyCardTransferOptions,
        CardRecommendation,
        PropertyCardMyPoints,
        PropertyCardStayPrice,
    },
    props: {
        propertyProgram: Object,
        perNightRate: { type: Number, required: true },
        layout: {
            type: String,
            defaut: 'horizontal',
        },
        urlParams: { type: Object },
    },
    data: () => ({
        cardContainer: null,
        cardContainerHeight: '75px',
    }),
    watch: {
        showFullCard(val) {
            if (this.layout === 'horizontal' && this.$refs.cardcontainer) {
                if (val) {
                    this.$refs.cardcontainer.style.height =
                        this.cardContainerHeight;
                } else {
                    this.$refs.cardcontainer.style.height = '75px';
                }
            }
        },
    },
    mounted() {
        if (this.layout === 'horizontal' && this.$refs.cardcontainer) {
            let element = getComputedStyle(this.$refs.cardcontainer);
            this.cardContainer = element;
            this.cardContainerHeight = this.cardContainer.height;
            this.$refs.cardcontainer.style.transition = 'height 0.25s ease';
            this.$refs.cardcontainer.style.height = '75px';

            window.addEventListener('resize', () => {
                if (this.$refs.cardgrid) {
                    let element2 = getComputedStyle(this.$refs.cardgrid);
                    this.cardContainerHeight = element2.height;
                }
            });
        }
    },
    setup(props) {
        const showFullCard = ref(false);
        const hotelsStore = useHotelsStore();
        const pointsStore = usePointsStore();
        const { stayLength } = storeToRefs(hotelsStore);
        const loading = ref(true);
        const usersWhoNeedCardRecs = ref([]);

        const userPoints = computed(() =>
            filterUserPoints(props.propertyProgram.id)
        );
        const noTransferPointsAvailable = computed(
            () => !userPoints.value.length
        );
        const { filterUserPoints, addEnoughPointsValues } =
            useRewardsAndTransferPartners();

        const colLayout = computed(() => {
            return props.layout;
        });

        const pointsForProgram = computed(() => {
            return pointsStore.filterDataByProgramId(props.propertyProgram.id);
        });

        const totalPointsCost = computed(() => {
            return Number(stayLength.value) * Number(props.perNightRate);
        });

        const widthSmall = computed(() => {
            if (colLayout.value === 'vertical') {
                return '100%';
            }
            return '28%';
        });
        const widthMedium = computed(() => {
            if (colLayout.value === 'vertical') {
                return '100%';
            }
            return '42%';
        });

        //points for programs that can be transferred to the property's program, by program:
        const filteredUserPoints = computed(() => {
            const userPointsAndCanAffordInfo = addEnoughPointsValues(
                totalPointsCost.value,
                pointsForProgram.value,
                userPoints.value
            );
            return userPointsAndCanAffordInfo;
        });

        const handlePointsCalculated = (userArray) => {
            usersWhoNeedCardRecs.value = userArray;
        };

        const toggleShowFullCard = () => {
            showFullCard.value = !showFullCard.value;
        };

        watchEffect(() => {
            if (props.propertyProgram) {
                loading.value = false;
            }
        });

        return {
            filteredUserPoints,
            loading,
            pointsForProgram,
            showFullCard,
            toggleShowFullCard,
            totalPointsCost,
            handlePointsCalculated,
            noTransferPointsAvailable,
            usersWhoNeedCardRecs,
            colLayout,
            widthSmall,
            widthMedium,
        };
    },
};
</script>

<style lang="scss" scoped>
.mask:hover {
    mask-image: linear-gradient(
        to top,
        transparent 15%,
        rgba(255, 255, 255, 0.75) 100%
    );
}
.mask {
    mask-image: linear-gradient(
        to top,
        transparent 40%,
        rgba(255, 255, 255, 0.75) 100%
    );
}
</style>
